import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook for tracking loading states and showing toast notifications
 * during API calls.
 * 
 * @returns {object} - Loading states and helper functions
 */
export function useLoadingToast() {
  const [loadingStates, setLoadingStates] = useState({});
  const toastIds = useRef({});

  /**
   * Show a loading toast for a specific operation
   */
  const showLoadingToast = useCallback((key, options = {}) => {
    const {
      title = 'Loading...',
      description = 'Please wait while we fetch the data',
    } = options;

    setLoadingStates(prev => ({ ...prev, [key]: true }));

    // Dismiss any existing toast for this key
    if (toastIds.current[key]) {
      toast.dismiss(toastIds.current[key]);
    }

    // Create new loading toast
    const toastId = toast.loading(title, {
      description,
    });

    toastIds.current[key] = toastId;

    return toastId;
  }, []);

  /**
   * Dismiss loading toast and show success
   */
  const showSuccessToast = useCallback((key, options = {}) => {
    const {
      title = 'Success',
      description = 'Operation completed successfully',
    } = options;

    setLoadingStates(prev => ({ ...prev, [key]: false }));

    // Dismiss loading toast
    if (toastIds.current[key]) {
      toast.dismiss(toastIds.current[key]);
      delete toastIds.current[key];
    }

    toast.success(title, {
      description,
    });
  }, []);

  /**
   * Dismiss loading toast and show error
   */
  const showErrorToast = useCallback((key, options = {}) => {
    const {
      title = 'Error',
      description = 'Something went wrong. Please try again.',
    } = options;

    setLoadingStates(prev => ({ ...prev, [key]: false }));

    // Dismiss loading toast
    if (toastIds.current[key]) {
      toast.dismiss(toastIds.current[key]);
      delete toastIds.current[key];
    }

    toast.error(title, {
      description,
    });
  }, []);

  /**
   * Dismiss loading toast without showing any notification
   */
  const dismissLoadingToast = useCallback((key) => {
    setLoadingStates(prev => ({ ...prev, [key]: false }));

    if (toastIds.current[key]) {
      toast.dismiss(toastIds.current[key]);
      delete toastIds.current[key];
    }
  }, []);

  /**
   * Check if a specific operation is loading
   */
  const isLoading = useCallback((key) => {
    return loadingStates[key] === true;
  }, [loadingStates]);

  /**
   * Check if any operation is loading
   */
  const isAnyLoading = useCallback(() => {
    return Object.values(loadingStates).some(v => v === true);
  }, [loadingStates]);

  /**
   * Wrap an async function with loading states and toast notifications
   */
  const withLoading = useCallback((key, asyncFn, options = {}) => {
    return async (...args) => {
      showLoadingToast(key, options);
      try {
        const result = await asyncFn(...args);
        showSuccessToast(key, options.successOptions || options);
        return result;
      } catch (error) {
        showErrorToast(key, {
          ...options,
          description: error.message || options.description
        });
        throw error;
      }
    };
  }, [showLoadingToast, showSuccessToast, showErrorToast]);

  return {
    loadingStates,
    toastIds: toastIds.current, // Expose current value if needed, though rarely used directly
    showLoadingToast,
    showSuccessToast,
    showErrorToast,
    dismissLoadingToast,
    isLoading,
    isAnyLoading,
    withLoading,
  };
}

/**
 * Higher-order component wrapper for components that need loading states
 * @param {React.Component} WrappedComponent - Component to wrap
 * @param {string} loadingKey - Unique key for this component's loading state
 * @param {object} options - Toast options
 */
export function withLoadingToast(WrappedComponent, loadingKey, options = {}) {
  return function WithLoadingToastComponent(props) {
    const loadingToast = useLoadingToast();

    return (
      <WrappedComponent
        {...props}
        loadingToast={loadingToast}
        loadingKey={loadingKey}
        loadingOptions={options}
      />
    );
  };
}

